import { FigureShot } from './FigureShot'

interface Props { cs: boolean }

function CsBody() {
  return (
    <>
      <p>
        ShowX používá <strong>LAN-first</strong> model: stanice se připojují přes lokální síť, bez cloudu, bez
        přihlašování k účtu. Celé párování trvá méně než minutu.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Jak stanice najde shell (mDNS)</h3>
      <p>
        Shell na FOH Macu se automaticky ohlásí na LAN přes <strong>mDNS</strong> (Bonjour) se service typem{' '}
        <code>_showx._tcp.local</code>. Na stejné Wi-Fi síti nebo drátovém segmentu stanice ShowX "uvidí" bez manuálního
        zadávání adresy.
      </p>
      <p>
        Pokud mDNS nefunguje (korporátní Wi-Fi, firewall, VPN), zadej adresu ručně:{' '}
        <code>http://&lt;ip-macu&gt;:5300</code>. IP adresu shellu najdeš v záložce Show → STATIONS panel.
      </p>
      <p>
        Shell zobrazuje také <strong>QR kód</strong> — rychlá cesta pro mobilní zařízení: naskenuj, otevře se
        pairing stránka.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Pairing flow — krok za krokem</h3>
      <FigureShot
        src="/shots/prirucka/05_pwa_pairing.png"
        alt="ShowX PWA — pairing screen s PIN inputem a výběrem role"
        caption="PWA · pairing screen · PIN input, jméno stanice, výběr role a departmentů"
      />
      <ol>
        <li>
          Na zařízení (iPad / Mac / Windows / Android) otevři v prohlížeči{' '}
          <code>http://&lt;ip-macu&gt;:5300</code> nebo naskenuj QR kód ze shellu.
        </li>
        <li>
          Otevře se <strong>pairing stránka</strong>. Zadej <strong>6-místný PIN</strong> zobrazený v shellu.
          V testovacím módu (SHOWX_PAIRING_TEST_PIN=123456) je PIN vždy stejný a nevyprší.
        </li>
        <li>
          Pojmenuj stanici (např. "SM iPad", "LX Pult") a vyber <strong>roli</strong>.
        </li>
        <li>
          Klikni <strong>Submit</strong> — stanice se spáruje a zobrazí příslušný view (SM master, operátor, countdown).
        </li>
      </ol>
      <p>
        Session je <strong>persistentní</strong>: token je uložen lokálně v browseru. Refresh stránky ani restart shellu
        tě o session nepřipraví. Yjs CRDT (data model shellu) se automaticky resynchronizuje.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Role stanic</h3>

      <h4 className="font-semibold mt-6 mb-2">Stage Manager (SM)</h4>
      <FigureShot
        src="/shots/prirucka/06_pwa_sm_master.png"
        alt="ShowX PWA — SM master view s celým cuelistem, playheadem a GO tlačítkem"
        caption="PWA · SM master view · celý cuelist, playhead, GO button, standby panel"
      />
      <p>
        SM vidí <strong>celý cuelist</strong>. Klíčové prvky SM view:
      </p>
      <ul>
        <li><strong>Cuelist</strong> — seznam všech cues bez omezení departmentu. Aktivní cue (next) je zvýrazněna.</li>
        <li><strong>GO button</strong> — velké GO tlačítko (nebo klávesová zkratka: Q pak Space). V SHOW módu je to hold-to-GO.</li>
        <li><strong>Standby panel</strong> — zobrazuje detail "next cue": label, departments, payloady (přehledový popis).</li>
        <li><strong>BACK</strong> — vrátí playhead o jednu cue zpět.</li>
        <li><strong>Mode badge</strong> — REHEARSAL / SHOW; SM klikne pro přepnutí.</li>
      </ul>
      <FigureShot
        src="/shots/prirucka/07_pwa_sm_standby.png"
        alt="ShowX PWA — SM standby panel s detailem vybrané cue"
        caption="PWA · SM view · standby panel — detail selected cue (departments, label)"
      />

      <h4 className="font-semibold mt-8 mb-2">Operator (per-department view)</h4>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-4 space-y-2">
          <div className="text-muted text-xs mb-3">PWA · LX operator view — filtrovaný cuelist jen pro LX</div>
          {[
            ['▶', '12', 'Světla — západ slunce', 'LX', 'armed'],
            ['○', '13', 'Světla — full black', 'LX', ''],
            ['○', '14', 'Světla — UE pink wash', 'LX', ''],
          ].map(([icon, num, label, dept, state]) => (
            <div key={num} className={`flex gap-3 items-center px-3 py-2 rounded ${state === 'armed' ? 'bg-accent/10 border border-accent/30' : ''}`}>
              <span className="text-accent-deep w-4">{icon}</span>
              <span className="text-muted w-6">{num}</span>
              <span className="text-ink flex-1">{label}</span>
              <span className="text-muted text-xs">{dept}</span>
              {state === 'armed' && <span className="text-accent-deep text-xs">STANDBY</span>}
            </div>
          ))}
        </div>
      </div>
      <p>
        Operátor vidí pouze cues pro svá oddělení (nastaveného při párování). Klíčové prvky:
      </p>
      <ul>
        <li><strong>Filtrovaný cuelist</strong> — jen cues kde department = operator's dept.</li>
        <li><strong>Standby highlight</strong> — aktuálně "armed" cue je vizuálně zvýrazněna (čeká na GO od SM).</li>
        <li><strong>Acknowledge</strong> — operátor potvrdí standby (cue light flow). SM vidí potvrzení.</li>
      </ul>

      <h4 className="font-semibold mt-8 mb-2">Countdown</h4>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono">
        <div className="bg-ink px-5 py-10 text-center">
          <div className="text-cream text-[3rem] font-semibold tracking-widest">00:47:23:12</div>
          <div className="text-cream/40 text-xs mt-2 tracking-[0.3em]">HH:MM:SS:FF · INTERNAL</div>
        </div>
        <div className="bg-ink/5 px-5 py-2 text-center text-muted text-xs">
          PWA · countdown view · velký timecode displej pro nástěnnou obrazovku
        </div>
      </div>
      <p>
        Countdown stanice zobrazuje velký timecode displej (HH:MM:SS:FF) — pro nástěnné obrazovky nebo Pi kiosky. Žádné
        ovládání, jen display. Ideální pro scénické pozice kde crew potřebuje vidět čas, ne cuelist.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Odparování stanice</h3>
      <p>
        Shell → záložka Show → STATIONS panel → klikni <strong>revoke</strong> u konkrétní stanice. Token se zneplatní,
        stanice bude přesměrována zpět na pairing stránku.
      </p>
      <p>
        Při párování v testovacím módu (pevný PIN) se stanice automaticky znovu připojí po refreshi — pracuje jako s
        produkčním PIN. Při produkčním nasazení se PIN automaticky rotuje a expiruje.
      </p>
    </>
  )
}

function EnBody() {
  return (
    <>
      <p>
        ShowX uses a <strong>LAN-first</strong> model: stations connect over the local network, no cloud, no account
        login. Pairing takes less than a minute.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">How a station finds the shell (mDNS)</h3>
      <p>
        The shell on the FOH Mac automatically announces itself on the LAN via <strong>mDNS</strong> (Bonjour) with
        service type <code>_showx._tcp.local</code>. On the same Wi-Fi network or wired segment, stations see ShowX
        without manual address entry.
      </p>
      <p>
        If mDNS doesn't work (corporate Wi-Fi, firewall, VPN), enter the address manually:{' '}
        <code>http://&lt;mac-ip&gt;:5300</code>. Find the shell's IP in the Show tab → STATIONS panel.
      </p>
      <p>
        The shell also shows a <strong>QR code</strong> — fast path for mobile: scan it, pairing page opens.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Pairing flow — step by step</h3>
      <FigureShot
        src="/shots/prirucka/05_pwa_pairing.png"
        alt="ShowX PWA — pairing screen with PIN input and role selector"
        caption="PWA · pairing screen · PIN input, station name, role and department picker"
      />
      <ol>
        <li>
          On a device (iPad / Mac / Windows / Android) open{' '}
          <code>http://&lt;mac-ip&gt;:5300</code> or scan the QR code from the shell.
        </li>
        <li>
          The <strong>pairing page</strong> opens. Enter the <strong>6-digit PIN</strong> shown in the shell.
          In test mode (SHOWX_PAIRING_TEST_PIN=123456) the PIN is always the same and never expires.
        </li>
        <li>Name the station (e.g. "SM iPad", "LX Desk") and pick a <strong>role</strong>.</li>
        <li>Click <strong>Submit</strong> — the station pairs and renders the appropriate view (SM master, operator, countdown).</li>
      </ol>
      <p>
        The session is <strong>persistent</strong>: the token is stored locally in the browser. A page refresh or shell
        restart does not lose your session. The Yjs CRDT (shell data model) auto-resyncs.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Station roles</h3>

      <h4 className="font-semibold mt-6 mb-2">Stage Manager (SM)</h4>
      <FigureShot
        src="/shots/prirucka/06_pwa_sm_master.png"
        alt="ShowX PWA — SM master view with full cuelist, playhead and GO button"
        caption="PWA · SM master view · full cuelist, playhead, GO button, standby panel"
      />
      <p>The SM sees the <strong>full cuelist</strong>. Key SM view elements:</p>
      <ul>
        <li><strong>Cuelist</strong> — all cues, no department filter. Active (next) cue is highlighted.</li>
        <li><strong>GO button</strong> — large GO button (or keyboard shortcut: Q then Space). In SHOW mode this becomes hold-to-GO.</li>
        <li><strong>Standby panel</strong> — shows the "next cue" detail: label, departments, payload summary.</li>
        <li><strong>BACK</strong> — moves the playhead one cue back.</li>
        <li><strong>Mode badge</strong> — REHEARSAL / SHOW; SM clicks to switch.</li>
      </ul>
      <FigureShot
        src="/shots/prirucka/07_pwa_sm_standby.png"
        alt="ShowX PWA — SM standby panel with selected cue detail"
        caption="PWA · SM view · standby panel — selected cue detail (departments, label)"
      />

      <h4 className="font-semibold mt-8 mb-2">Operator (per-department view)</h4>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-4 space-y-2">
          <div className="text-muted text-xs mb-3">PWA · LX operator view — department-filtered cuelist</div>
          {[
            ['▶', '12', 'Lights — sunset', 'LX', 'armed'],
            ['○', '13', 'Lights — full black', 'LX', ''],
            ['○', '14', 'Lights — UE pink wash', 'LX', ''],
          ].map(([icon, num, label, dept, state]) => (
            <div key={num} className={`flex gap-3 items-center px-3 py-2 rounded ${state === 'armed' ? 'bg-accent/10 border border-accent/30' : ''}`}>
              <span className="text-accent-deep w-4">{icon}</span>
              <span className="text-muted w-6">{num}</span>
              <span className="text-ink flex-1">{label}</span>
              <span className="text-muted text-xs">{dept}</span>
              {state === 'armed' && <span className="text-accent-deep text-xs">STANDBY</span>}
            </div>
          ))}
        </div>
      </div>
      <p>An operator sees only cues for their department(s) (set during pairing). Key elements:</p>
      <ul>
        <li><strong>Filtered cuelist</strong> — only cues where department = operator's dept.</li>
        <li><strong>Standby highlight</strong> — currently "armed" cue is visually highlighted (waiting for SM's GO).</li>
        <li><strong>Acknowledge</strong> — operator confirms standby (cue light flow). SM sees the ack.</li>
      </ul>

      <h4 className="font-semibold mt-8 mb-2">Countdown</h4>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono">
        <div className="bg-ink px-5 py-10 text-center">
          <div className="text-cream text-[3rem] font-semibold tracking-widest">00:47:23:12</div>
          <div className="text-cream/40 text-xs mt-2 tracking-[0.3em]">HH:MM:SS:FF · INTERNAL</div>
        </div>
        <div className="bg-ink/5 px-5 py-2 text-center text-muted text-xs">
          PWA · countdown view · large timecode display for wall screen
        </div>
      </div>
      <p>
        The countdown station shows a large timecode display (HH:MM:SS:FF) — for wall screens or Pi kiosks. No
        controls, display only. Ideal for scenic positions where crew needs to see time, not the cuelist.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Revoking a station</h3>
      <p>
        Shell → Show tab → STATIONS panel → click <strong>revoke</strong> next to the station. The token is
        invalidated; the station redirects to the pairing page.
      </p>
      <p>
        In test mode (fixed PIN) the station auto-reconnects on refresh — behaves like a production PIN. In production
        the PIN rotates automatically and expires.
      </p>
    </>
  )
}

export default function Chapter4({ cs }: Props) {
  return (
    <article id="kap-4" className="scroll-mt-24">
      <h2 className="display-serif text-display-2 leading-tight mb-8">
        {cs ? '4. Stanice a pairing' : '4. Stations and pairing'}
      </h2>
      <div className="copy prose-guide max-w-none">
        {cs ? <CsBody /> : <EnBody />}
      </div>
    </article>
  )
}
